import * as dateFns from "date-fns";
import {
  differenceInDays,
  format,
  getYear,
  subWeeks,
  startOfISOWeek,
} from "date-fns";
import { Dropdown } from "primereact/dropdown";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDataContext } from "../../../hooks/useDataContext";
import ProfileDropdown from "../../compound/ProfileDropdown";
import Arrow from "../../../assets/images/down-arrow.svg";
import "primeicons/primeicons.css";
import NextBtn from "./Button/NextBtn";
import PrevBtn from "./Button/PrevBtn";
import AdvancedFilterOptions from './AdvancedFilter/Index';
import "../DashboardFilters/dashboardFilters.scss";


const DashboardFilters = ({ 
  showTypeFilter = true,
  showServiceManagerFilter = true,
  showPartnerFilter = true,
  showLansdelFilter = true,
  showDateFilter = true,
  showProfile = true,
  selectedView = null
}) => {
  const { filterOptions, filters, dispatch } = useDataContext();
  const { startDate, endDate } = filters;
  const [filterActive, setFilterActive] = useState(false);
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);
  const currentDate = new Date();
  const daysDifference = useMemo(
    () => differenceInDays(endDate, startDate) + 1,
    [startDate, endDate]
  );
  const dropdownRef = useRef(null);
  const filterWrapperRef = useRef(null);
  const yearOptions = [];
  for (let i = getYear(currentDate) - 10; i <= getYear(currentDate) + 10; i++) {
    yearOptions.push({ label: `${i}`, value: i });
  }

  // Safely get filter options with fallback to empty array
  // Handle both naming conventions: managers/service_managers and landsdel/lansdel
  const safeFilterOptions = useMemo(() => {
    if (!filterOptions) {
      return {
        types: [],
        service_managers: [],
        partners: [],
        lansdel: [],
        inquiry_status: [],
        task_status: []
      };
    }
    return {
      types: Array.isArray(filterOptions.types) ? filterOptions.types : [],
      service_managers: Array.isArray(filterOptions.service_managers) 
        ? filterOptions.service_managers 
        : Array.isArray(filterOptions.managers) 
          ? filterOptions.managers 
          : [],
      partners: Array.isArray(filterOptions.partners) ? filterOptions.partners : [],
      lansdel: Array.isArray(filterOptions.lansdel) 
        ? filterOptions.lansdel 
        : Array.isArray(filterOptions.landsdel) 
          ? filterOptions.landsdel 
          : [],
      inquiry_status: Array.isArray(filterOptions.inquiry_status) ? filterOptions.inquiry_status : [],
      task_status: Array.isArray(filterOptions.task_status) ? filterOptions.task_status : []
    };
  }, [filterOptions]);

  // toggle  advance filter
  const toggleFilter = () => {
    setFilterActive(!filterActive);
  };
  // filter actions
  const handleChange = (filterType) => (event) => {
    // PrimeReact Dropdown onChange provides an event object with the selected value in event.value
    const selectedOption = event?.value ?? event;
    console.log(`Selected option for ${filterType}:`, selectedOption);
    
    // When X button is clicked, selectedOption is null - always set filter to null
    if (selectedOption === null || selectedOption === undefined) {
      dispatch({
        type: "SET_FILTERS",
        payload: { ...filters, [filterType]: null },
      });
      return;
    }

    // Extract only serializable properties to avoid circular reference errors
    const extractCleanValue = (option) => {
      if (!option || typeof option !== 'object') {
        return option;
      }
      
      // For status filters, extract id and value
      if (filterType === 'task_status' || filterType === 'inquiry_status') {
        return {
          id: option.id,
          value: option.value
        };
      }
      
      // For other filters, extract only serializable properties
      // If option has a nested 'value' object, flatten it by extracting its properties
      const clean = {};
      
      // If option.value is an object, extract its properties and merge with option's direct properties
      if (option.value && typeof option.value === 'object' && !Array.isArray(option.value)) {
        // Flatten nested value object
        if (option.value.id !== undefined) clean.id = option.value.id;
        if (option.value.name !== undefined) clean.name = option.value.name;
        if (option.value.value !== undefined) clean.value = option.value.value;
        if (option.value.color !== undefined) clean.color = option.value.color;
        if (option.value.item_id !== undefined) clean.item_id = option.value.item_id;
      }
      
      // Also extract direct properties from option (these take precedence if they exist)
      if (option.id !== undefined) clean.id = option.id;
      if (option.name !== undefined) clean.name = option.name;
      if (option.value !== undefined && typeof option.value !== 'object') clean.value = option.value;
      if (option.label !== undefined) clean.label = option.label;
      if (option.color !== undefined) clean.color = option.color;
      if (option.item_id !== undefined) clean.item_id = option.item_id;
      
      // If we extracted properties, return the clean object, otherwise return null
      if (Object.keys(clean).length > 0) {
        return clean;
      }
      
      // Fallback: return null if we can't extract clean data
      return null;
    };

    let filterValue = null;
    if (selectedOption) {
      if (filterType === 'task_status' || filterType === 'inquiry_status') {
        // For status filters, the selectedOption might be a string (the value/name) or an object
        // First, try to find the matching option from the options array
        const statusOptions = filterType === 'task_status' 
          ? safeFilterOptions.task_status 
          : safeFilterOptions.inquiry_status;
        
        // Check if status uses 'name' instead of 'value' (for both task_status and inquiry_status)
        const useName = statusOptions.length > 0 && statusOptions[0]?.name !== undefined;
        
        let matchedOption = null;
        
        // If selectedOption is already an object with id and value/name, use it
        if (selectedOption && typeof selectedOption === 'object' && selectedOption.id !== undefined && (selectedOption.value !== undefined || selectedOption.name !== undefined)) {
          matchedOption = selectedOption;
        } 
        // If selectedOption is a string (the value/name), find the matching option
        else if (typeof selectedOption === 'string') {
          matchedOption = statusOptions.find(opt => {
            if (useName) {
              return opt.name === selectedOption || opt.value === selectedOption;
            }
            return opt.value === selectedOption || opt.name === selectedOption;
          });
        }
        // If selectedOption is an object but missing id, try to match by value/name
        else if (selectedOption && typeof selectedOption === 'object') {
          const searchValue = selectedOption.name || selectedOption.value;
          if (searchValue !== undefined) {
            matchedOption = statusOptions.find(opt => {
              if (useName) {
                return (opt.name === searchValue || opt.value === searchValue) || opt.id === selectedOption.id;
              }
              return (opt.value === searchValue || opt.name === searchValue) || opt.id === selectedOption.id;
            });
          }
        }
        
        if (matchedOption) {
          filterValue = {
            id: matchedOption.id,
            name: matchedOption.name,
            value: matchedOption.name || matchedOption.value
          };
        }
      } else {
        // For other filters, extract clean serializable data
        filterValue = extractCleanValue(selectedOption);
      }
    }
    
    dispatch({
      type: "SET_FILTERS",
      payload: { ...filters, [filterType]: filterValue },
    });
  };

  const toggleDropdown = () => {
    setDropdownVisible(!dropdownVisible);
  };

  const clearAdvancedFilter = () => {
    dispatch({
      type: "SET_FILTERS",
      payload: {
        ...filters,
        startDate: startOfISOWeek(subWeeks(new Date(), 5)),
        endDate: dateFns.addDays(new Date(), 90),
        selectedFilter: null,
        getSelectedMonth: null,
        getSelectedYear: null,
        getSelectedWeekNumbers: null,
      },
    });
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if the clicked element is within the PrimeReact dropdown
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        !event.target.closest(".p-dropdown-items-wrapper") &&
        !event.target.closest(".date-filter-dropdown-arrow.active")
      ) {
        setDropdownVisible(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Track window width
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Handle body click to close filter on screens < 1024px
  useEffect(() => {
    const handleBodyClick = (event) => {
      // Only handle on screens less than 1024px
      if (windowWidth >= 1024) {
        return;
      }

      // Check if click is outside the filter wrapper and filter label
      if (
        filterActive &&
        filterWrapperRef.current &&
        !filterWrapperRef.current.contains(event.target) &&
        !event.target.closest(".filter-label")
      ) {
        setFilterActive(false);
      }
    };

    if (filterActive) {
      document.addEventListener("mousedown", handleBodyClick);
    }

    return () => {
      document.removeEventListener("mousedown", handleBodyClick);
    };
  }, [filterActive, windowWidth]);

  return (
    <div className="main-header">
      <section className="filter">
        <div className="container">
          {/* General filters */}
          {(showTypeFilter || showServiceManagerFilter || showPartnerFilter || showLansdelFilter) && (
            <div className="filter-wrapper">
              <div
                className="filter-label"
                onClick={() => {
                  if (windowWidth < 1024) {
                    toggleFilter();
                  }
                }}>
                <i className="pi pi-filter" style={{ fontSize: "1rem" }}></i>
                <p>Filter</p>
              </div>
              <div
                ref={filterWrapperRef}
                className={`filter-col__wrapper ${filterActive ? "active" : ""}`}>
                {filterActive && windowWidth < 1024 && (
                  <button
                    className="filter-close-icon"
                    onClick={() => setFilterActive(false)}
                    aria-label="Close filter">
                    <i className="pi pi-times" style={{ fontSize: "1.2rem" }}></i>
                  </button>
                )}
              {showTypeFilter && selectedView !== 'enquiry' && (
                <div className="filter-col">
                  <Dropdown
                    showClear
                    value={filters.type}
                    options={safeFilterOptions.types}
                    optionLabel="name"
                    placeholder="Type"
                    className="w-full "
                    onChange={handleChange("type")}
                    dataKey="id"
                  />
                </div>
              )}
              {showServiceManagerFilter && selectedView !== 'enquiry' && (
                <div className="filter-col">
                  <Dropdown
                    showClear
                    value={filters.service_managers}
                    options={safeFilterOptions.service_managers}
                    optionLabel="name"
                    placeholder="Service Manager"
                    className="w-full "
                    onChange={handleChange("service_managers")}
                    dataKey="id"
                  />
                </div>
              )}
              {showPartnerFilter && (
                <div className="filter-col">
                  <Dropdown
                    showClear
                    value={filters.partner}
                    options={safeFilterOptions.partners}
                    optionLabel="name"
                    placeholder="Partner"
                    className="w-full"
                    onChange={handleChange("partner")}
                    dataKey="id"
                  />
                </div>
              )}
              {showLansdelFilter && (
                <div className="filter-col">
                  <Dropdown
                    showClear
                    value={filters.lansdel}
                    options={safeFilterOptions.lansdel}
                    optionLabel={safeFilterOptions.lansdel.length > 0 && safeFilterOptions.lansdel[0]?.value !== undefined ? "value" : "name"}
                    placeholder="Landsdel"
                    className="w-full "
                    onChange={handleChange("lansdel")}
                    dataKey="id"
                  />
                </div>
              )}
              {selectedView === 'enquiry' && safeFilterOptions.inquiry_status.length > 0 && (
                <div className="filter-col">
                  <Dropdown
                    showClear
                    value={filters.inquiry_status}
                    options={safeFilterOptions.inquiry_status}
                    optionLabel={safeFilterOptions.inquiry_status.length > 0 && safeFilterOptions.inquiry_status[0]?.name !== undefined ? "name" : "value"}
                    placeholder="Status"
                    className="w-full "
                    onChange={handleChange("inquiry_status")}
                    dataKey="id"
                  />
                </div>
              )}
              {selectedView === 'task' && safeFilterOptions.task_status.length > 0 && (
                <div className="filter-col">
                  <Dropdown
                    showClear
                    value={filters.task_status}
                    options={safeFilterOptions.task_status}
                    optionLabel={safeFilterOptions.task_status.length > 0 && safeFilterOptions.task_status[0]?.name !== undefined ? "name" : "value"}
                    placeholder="Status"
                    className="w-full "
                    onChange={handleChange("task_status")}
                    dataKey="id"
                  />
                </div>
              )}
              </div>
            </div>
          )}

          {/* data filter dropdown */}
          {showDateFilter && (
            <div className="date-filter">
              {/* Date switcher */}
              <div className="date-filter-head">
                <div className="date-filter-arrows">
                  <PrevBtn daysDifference={daysDifference} />
                  <NextBtn daysDifference={daysDifference} />
                </div>
                <div className="show-date" onClick={() => clearAdvancedFilter()}>
                  {/* Display start and end dates */}
                  {startDate && endDate && (
                    <div className="date-range-preview">
                      <p>{format(new Date(), "LLL dd yyyy")}</p>
                    </div>
                  )}
                </div>
                {/* date range filter -dropdown */}
                <div
                  className={`date-filter-dropdown-arrow ${
                    !dropdownVisible ? "inactive" : "active"
                  }`}
                  onClick={toggleDropdown}>
                  <img src={Arrow} alt="arrow" />
                </div>

                {/* Dropdown list */}
                {dropdownVisible && (
                  <div className="date-filter-dropdown" ref={dropdownRef}>
                    <h4>Vis overblik for</h4>
                    <AdvancedFilterOptions />
                  </div>
                )}
              </div>
            </div>
          )}
          {showProfile && (
            <div className="profile">
              <ProfileDropdown />
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default DashboardFilters;
